import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import AspectRatio from '@ep/components/AspectRatio';
import theme from '@ep/theme';
import { Asset, RichText } from '@ep/types/apolloTypes';
import { Gcms_Asset, Gcms_RichText } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import 'lazysizes';
import React, { useEffect, useRef } from 'react';

export interface MediaProps extends React.HTMLAttributes<HTMLImageElement> {
  media: Gcms_Asset | Asset;
  aspectRatio?: number;
  caption?: Gcms_RichText | RichText | null;
  cover?: boolean;
  rounded?: boolean;
  fill?: boolean;
  lazy?: boolean;
  lightbox?: boolean;
}

interface WrapperProps {
  fill?: boolean;
}

const Wrapper = styled('div', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'fill',
})<WrapperProps>(
  ({ fill }) => `
    display: inline-flex;
    flex-direction: column;
    ${fill ? 'width: 100%; height: 100%;' : ''}
  `,
);

type ImageProps = Omit<MediaProps, 'media'>;

const Image = styled('img', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'fill',
})<ImageProps>(
  ({ cover, rounded }) => `
    flex: 1;
    min-width: 0;
    width: 100%;
    height: 100%;
    object-fit: ${cover ? 'cover' : 'contain'};
    ${rounded ? `border-radius: ${theme.radius.small};` : ''}
  `,
);

const StyledVideo = styled.video`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const Caption = styled.div`
  margin-top: 0.5rem;
  font-size: 1rem;
  font-style: italic;
  color: ${theme.colors.darkGray};
`;

function Media({
  media,
  aspectRatio,
  caption,
  className,
  fill,
  lazy,
  lightbox,
  ...rest
}: MediaProps) {
  // If media empty skip the template altogether
  if (media == null) {
    return <div className="no-media"></div>;
  }

  if (media.vidyardLink === 'video') {
    return (
      <Wrapper className={className} fill={fill}>
        <StyledVideo autoPlay loop playsInline preload="auto" muted>
          <source src={media.url} type="video/mp4" />
        </StyledVideo>
      </Wrapper>
    );
  }

  // Force src update on client side because React SSR
  // re-hydration does not update dom attributes
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (imgRef.current) {
      if (imgRef.current.src !== media.url && !media.vidyardLink) {
        imgRef.current.src = media.url;
      }

      if (
        typeof window !== undefined &&
        window.VidyardV4 &&
        media.vidyardLink
      ) {
        window.VidyardV4.api.renderPlayer(imgRef.current);
      }
    }
  }, [imgRef]);

  const vidyardProps: { [key: string]: string } = {};

  if (media.vidyardLink) {
    if (media.vidyardLink.search('youtube') != -1) {
      if (aspectRatio) {
        return (
          <AspectRatio
            ratio={aspectRatio}
            className={className}
            aria-label={media.altText}
          >
            <iframe width="100%" height="100%" src={media.vidyardLink}></iframe>
            {caption && <Caption {...getHtmlProp(caption.html)} />}
          </AspectRatio>
        );
      }

      return (
        <Wrapper className={className} fill={fill} aria-label={media.altText}>
          <iframe width="100%" height="100%" src={media.vidyardLink}></iframe>
          {caption && <Caption {...getHtmlProp(caption.html)} />}
        </Wrapper>
      );
    }

    const indexStart = media.vidyardLink.lastIndexOf('/');
    const indexEnd = media.vidyardLink.lastIndexOf('.');
    const dataUuid = media.vidyardLink.substring(indexStart + 1, indexEnd);

    vidyardProps.className = 'vidyard-player-embed';
    vidyardProps.src = media.vidyardLink;
    vidyardProps['data-uuid'] = dataUuid;
    vidyardProps['data-v'] = '4';
    vidyardProps['data-type'] = lightbox ? 'lightbox' : 'inline';
    vidyardProps['data-disable_ctas'] = '1';
    vidyardProps['preload'] = 'none';
    vidyardProps['new_visitor'] = '0';
  }

  const imageProps: { [key: string]: unknown } = {
    ref: imgRef,
    alt: media.altText || undefined,
    options: media.options || undefined,
  };

  if (lazy) {
    imageProps['data-src'] = media.url;
    imageProps.className = 'lazyload';
  } else {
    imageProps.src = media.url;
  }

  if (aspectRatio) {
    return (
      <AspectRatio ratio={aspectRatio} className={className}>
        <Image {...imageProps} {...vidyardProps} {...rest} />
        {caption && <Caption {...getHtmlProp(caption.html)} />}
      </AspectRatio>
    );
  }

  return (
    <Wrapper className={className} fill={fill}>
      <Image fill={fill} {...imageProps} {...vidyardProps} {...rest} />
      {caption && <Caption {...getHtmlProp(caption.html)} />}
    </Wrapper>
  );
}

export default Media;
